import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { usePlaidLink, type PlaidLinkOnSuccess, type PlaidLinkOnExit } from "react-plaid-link";
import { Loader2, Briefcase, CheckCircle2, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { applicantApi, type ApiError } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { trackPayrollFunnel } from "@/lib/payrollFunnel";

interface PlaidLinkButtonProps {
  /** Verify-flow session token — used to authenticate the link-token + exchange calls. */
  token: string | undefined;
  /**
   * Which Plaid product this button drives:
   *  - "bank" (default): Identity (+ bank income) — exchanges the public_token.
   *  - "payroll": dedicated Payroll Income session — NO public_token exchange;
   *    income arrives asynchronously via the backend INCOME webhook.
   */
  product?: "bank" | "payroll";
  /**
   * For product="payroll", which flow to open:
   *  - "digital" (default): connect a payroll provider. Confidence 0.95.
   *  - "document": Plaid's hosted pay-stub UPLOAD module. Confidence 0.70,
   *    parsing takes minutes–hours rather than seconds.
   *
   * Ignored for product="bank".
   */
  mode?: "digital" | "document";
  /** Whether this tile already shows a successful connection. Disables the button + flips the label. */
  connected?: boolean;
  /** Fired after a successful connection so the parent can refresh / mark the tile connected. */
  onSuccess?: (result?: { item_id: string; signals_written: Array<{ signal_type: string; confidence: number; source: string }> }) => void;
  /**
   * Fired when Plaid Link closes WITHOUT a success callback — the applicant's
   * provider wasn't listed, login failed, or they simply bailed. Deliberately
   * not limited to Plaid error codes: "I searched, my provider wasn't there, I
   * gave up" often produces no error at all, yet that's exactly the population
   * the upload fallback is for. The parent uses this to reveal State B.
   */
  onExitWithoutSuccess?: () => void;
  /**
   * Render the button itself. Pass `false` to mount this component headlessly
   * and drive it from `triggerRef` — used by the "Prefer to upload documents
   * instead?" text link, which must stay a muted link rather than a button.
   * (`FacebookLoginButton` used the same pattern on step 3 until platform #424
   * made Facebook a real OAuth provider and the component was deleted.)
   */
  renderTrigger?: boolean;
  /** Populated with a function that opens Plaid Link, for `renderTrigger={false}` callers. */
  triggerRef?: MutableRefObject<(() => void) | null>;
  /**
   * How long the payroll "Retrieving your income…" pending state persists
   * before it auto-reverts to the connect CTA. Income arrives asynchronously
   * via the backend INCOME webhook, so if it never lands (or the applicant's
   * consent predates the employment scope) we don't want to strand the button
   * forever. Defaults to 10 minutes for the digital flow and 4 hours for
   * document uploads (Plaid OCR is minutes–hours); set to 0 to disable.
   */
  pendingTimeoutMs?: number;
}

const DEFAULT_PENDING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// Document parsing is minutes–hours, not seconds (docs/SCORING.md), so the
// digital flow's 10-minute window would expire long before a legitimate upload
// finishes and would tell the applicant something had gone wrong when it
// hadn't. Submission never blocks on this either way.
const DOCUMENT_PENDING_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Plaid Link drop-in for the verify flow's Financial Accounts tile.
 *
 * Lifecycle (redesigned per step-3 plan — link-token fetched on click,
 * never on mount, so the idle state is the dark CTA, not a spinner):
 *  1. User clicks the idle "Connect Bank or Payroll Account" button.
 *  2. We POST /verify/plaid/link-token to get a one-shot Plaid link_token
 *     and mark `pendingOpen` so the effect below opens Plaid Link as soon
 *     as it's ready.
 *  3. usePlaidLink wraps Plaid's hosted modal; the effect calls `open()`
 *     when `linkToken && pendingOpen && ready`.
 *  4. onSuccess (from Plaid Link) → POST /verify/plaid/exchange. Backend
 *     persists the access_token encrypted, synchronously fetches Payroll
 *     Income + Identity, and writes attestations. We call the parent's
 *     onSuccess so the tile flips to its connected state.
 *  5. onExit / cancel → no signal written, link-token is single-use so we
 *     clear it; next click fetches a fresh one.
 */
export function PlaidLinkButton({
  token,
  product = "bank",
  mode = "digital",
  connected = false,
  onSuccess,
  onExitWithoutSuccess,
  renderTrigger = true,
  triggerRef,
  pendingTimeoutMs,
}: PlaidLinkButtonProps) {
  const isDocumentUpload = product === "payroll" && mode === "document";
  const effectiveTimeoutMs =
    pendingTimeoutMs ??
    (isDocumentUpload ? DOCUMENT_PENDING_TIMEOUT_MS : DEFAULT_PENDING_TIMEOUT_MS);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const openedRef = useRef(false);

  // Payroll income is webhook-driven: there's an async gap between Plaid Link
  // closing and our INCOME webhook writing employment.verification. We show a
  // "Retrieving your income…" pending state during that window, persisted in
  // sessionStorage (as the start timestamp) so it survives the applicant
  // navigating away from and back to the step (which remounts this button).
  // Cleared once `connected` flips, or after `effectiveTimeoutMs` elapses so the
  // button can't be stranded forever if the webhook never lands.
  //
  // The two flows keep SEPARATE keys: their timeouts differ by more than an
  // order of magnitude, so a shared key would let the digital button's 10-minute
  // window prune a document upload that is legitimately still parsing.
  const pendingKey = product === "payroll" && token
    ? `plaid_payroll${isDocumentUpload ? "_doc" : ""}_pending_${token}`
    : null;

  // Companion key recording that a pending window ENDED without the signal
  // landing. Without this the ending is invisible on a remount: the timeout
  // toast below only fires in a tab that stayed open, so an applicant who
  // navigated away and came back just saw the button revert to the connect
  // CTA — indistinguishable from never having connected at all.
  const expiredKey = pendingKey ? `${pendingKey}_expired` : null;

  // Returns true if a persisted pending start exists and is still within the
  // timeout window. An expired entry is pruned AND promoted to the expired
  // marker, so the note survives the remount that pruned it.
  const readPersistedPending = (): boolean => {
    if (typeof window === "undefined" || !pendingKey) return false;
    const raw = window.sessionStorage.getItem(pendingKey);
    if (!raw) return false;
    const started = Number(raw);
    if (!Number.isFinite(started)) {
      window.sessionStorage.removeItem(pendingKey);
      return false;
    }
    if (effectiveTimeoutMs > 0 && Date.now() - started >= effectiveTimeoutMs) {
      window.sessionStorage.removeItem(pendingKey);
      if (expiredKey) window.sessionStorage.setItem(expiredKey, "1");
      return false;
    }
    return true;
  };

  const readPersistedExpired = (): boolean => {
    if (typeof window === "undefined" || !expiredKey) return false;
    return window.sessionStorage.getItem(expiredKey) === "1";
  };

  const [payrollPending, setPayrollPending] = useState<boolean>(readPersistedPending);
  const [payrollExpired, setPayrollExpired] = useState<boolean>(readPersistedExpired);

  const handleSuccess = useCallback<PlaidLinkOnSuccess>(async (public_token, metadata) => {
    if (!token) return;

    // Payroll digital income has NO public_token exchange — Plaid pushes the
    // result to our INCOME webhook, which writes employment.verification
    // asynchronously. So we just acknowledge and let the parent refresh.
    if (product === "payroll") {
      // Uploads land the same way, just slower — set the expectation honestly
      // rather than implying it'll appear "shortly". Nothing about submission
      // waits on either.
      toast(
        isDocumentUpload
          ? {
              title: "Documents uploaded",
              description:
                "We're reviewing them now. This can take a while, so go ahead and continue — you don't need to wait here.",
            }
          : {
              title: "Payroll account connected",
              description: "We're retrieving your income details — this can take a moment and will appear shortly.",
            },
      );
      if (isDocumentUpload) trackPayrollFunnel("payroll_upload_complete", "document");
      if (pendingKey) window.sessionStorage.setItem(pendingKey, String(Date.now()));
      // A fresh attempt supersedes any previous unresolved one.
      if (expiredKey) window.sessionStorage.removeItem(expiredKey);
      setPayrollExpired(false);
      setPayrollPending(true);
      onSuccess?.();
      setLinkToken(null);
      openedRef.current = false;
      return;
    }

    setExchanging(true);
    try {
      const result = await applicantApi.plaidExchange(token, {
        public_token,
        metadata: metadata as unknown as Record<string, unknown>,
      });
      const wroteSomething = result.signals_written.length > 0;
      toast({
        title: wroteSomething ? "Income / identity verified" : "Account linked",
        description: wroteSomething
          ? `${result.signals_written.length} signal${result.signals_written.length === 1 ? "" : "s"} recorded from ${metadata.institution?.name ?? "your institution"}.`
          : "Account linked — verification data is still processing and will appear shortly.",
      });
      onSuccess?.(result);
    } catch (e) {
      const err = e as ApiError;
      toast({
        title: "Couldn't save Plaid connection",
        description: err.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setExchanging(false);
      // Plaid link_tokens are single-use — clear so the next click fetches fresh.
      setLinkToken(null);
      openedRef.current = false;
    }
  }, [token, product, isDocumentUpload, onSuccess, pendingKey, expiredKey]);

  const handleExit = useCallback<PlaidLinkOnExit>(() => {
    // Clear the single-use token so a subsequent click fetches a fresh one
    // instead of failing silently.
    setLinkToken(null);
    setPendingOpen(false);
    openedRef.current = false;

    // Plaid only calls onExit when Link closed WITHOUT success (onSuccess owns
    // the happy path), so reaching here IS the exit-without-success event — no
    // need to inspect the error argument, and deliberately so: a missing
    // provider usually produces no error code at all.
    if (product === "payroll") {
      trackPayrollFunnel("payroll_exit_without_success", mode);
      onExitWithoutSuccess?.();
    }
  }, [product, mode, onExitWithoutSuccess]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
  });

  // When the user clicked the button and the token has arrived and Plaid Link
  // is ready, open the hosted modal once. The `openedRef` guard prevents
  // re-opening on rerenders.
  useEffect(() => {
    if (pendingOpen && linkToken && ready && !openedRef.current) {
      openedRef.current = true;
      setPendingOpen(false);
      open();
    }
  }, [pendingOpen, linkToken, ready, open]);

  // Rehydrate the pending flag once the token is known — the button may mount
  // after the token arrives (e.g. the applicant returns to this step). Honors
  // the timeout so an expired entry doesn't re-strand the button, and prunes it.
  useEffect(() => {
    if (!pendingKey) {
      setPayrollPending(false);
      return;
    }
    const raw = window.sessionStorage.getItem(pendingKey);
    const started = raw ? Number(raw) : NaN;
    const active =
      Number.isFinite(started) && (effectiveTimeoutMs <= 0 || Date.now() - started < effectiveTimeoutMs);
    if (raw && !active) {
      window.sessionStorage.removeItem(pendingKey);
      if (expiredKey) window.sessionStorage.setItem(expiredKey, "1");
      setPayrollExpired(true);
    }
    setPayrollPending(active);
  }, [pendingKey, expiredKey, effectiveTimeoutMs]);

  // Auto-clear the pending state after the timeout so the button can't be
  // stranded on "Retrieving your income…" if the webhook never arrives. The
  // timer is anchored to the persisted start time, so it survives remounts.
  useEffect(() => {
    if (!payrollPending || !pendingKey || effectiveTimeoutMs <= 0) return;
    const raw = window.sessionStorage.getItem(pendingKey);
    const started = raw ? Number(raw) : Date.now();
    const remaining = Math.max(0, effectiveTimeoutMs - (Date.now() - started));
    const timer = window.setTimeout(() => {
      window.sessionStorage.removeItem(pendingKey);
      if (expiredKey) window.sessionStorage.setItem(expiredKey, "1");
      setPayrollExpired(true);
      setPayrollPending(false);
      toast(
        isDocumentUpload
          ? {
              title: "Still reviewing your documents",
              description:
                "This is taking longer than expected. Nothing is blocked — your income will appear automatically if it arrives, and you can upload again if you'd like.",
            }
          : {
              title: "Still retrieving your income",
              description:
                "This is taking longer than expected. You can reconnect, or continue — your income will still appear automatically if it arrives.",
            },
      );
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [payrollPending, pendingKey, expiredKey, effectiveTimeoutMs, isDocumentUpload]);

  // The employment.verification signal has landed (parent flipped `connected`)
  // — clear both the pending state and the expired note. This is also the path
  // a LATE arrival takes: a slow webhook, or the 0.B reconciliation sweep
  // repairing one that never came. Either way the note must disappear.
  useEffect(() => {
    if (!connected) return;
    if (payrollPending) setPayrollPending(false);
    if (payrollExpired) setPayrollExpired(false);
    if (pendingKey) window.sessionStorage.removeItem(pendingKey);
    if (expiredKey) window.sessionStorage.removeItem(expiredKey);
  }, [connected, payrollPending, payrollExpired, pendingKey, expiredKey]);

  const handleClick = useCallback(async () => {
    if (!token || connected) return;
    if (linkToken) {
      // Already have a token — open immediately.
      if (ready && !openedRef.current) {
        openedRef.current = true;
        open();
      } else {
        setPendingOpen(true);
      }
      return;
    }
    setLoadingToken(true);
    setPendingOpen(true);
    if (product === "payroll") trackPayrollFunnel("payroll_connect_attempt", mode);
    try {
      const res = product === "payroll"
        ? await applicantApi.plaidPayrollLinkToken(token, mode)
        : await applicantApi.plaidLinkToken(token);
      setLinkToken(res.link_token);
    } catch (e) {
      const err = e as ApiError;
      setPendingOpen(false);
      toast({
        title: isDocumentUpload ? "Couldn't open document upload" : "Couldn't open Financial Verification",
        description:
          err.status === 503
            ? "Plaid is not configured for this environment yet."
            : // 404 = the env's flag is off. The FE flag should already have
              // hidden this control, so a 404 means the two are out of sync.
              err.status === 404 && isDocumentUpload
              ? "Document upload isn't available for this environment yet."
              : err.message ?? "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setLoadingToken(false);
    }
  }, [token, product, mode, isDocumentUpload, connected, linkToken, ready, open]);

  // Expose the opener for headless callers (renderTrigger={false}).
  useEffect(() => {
    if (!triggerRef) return;
    triggerRef.current = () => void handleClick();
    return () => {
      triggerRef.current = null;
    };
  }, [triggerRef, handleClick]);

  const busy = loadingToken || exchanging || (pendingOpen && !ready);
  const disabled = !token || busy || connected || payrollPending;

  if (!renderTrigger) return null;

  // The pending window ended without the signal landing. This note is the
  // whole point of 0.C: the button reverting to its connect CTA on its own is
  // indistinguishable from never having connected, and the timeout toast only
  // ever reached a tab that stayed open.
  //
  // Deliberately calm and non-accusatory — nothing went wrong on the
  // applicant's side, and income may still arrive later (a slow webhook, or
  // the backend reconciliation sweep). It must NOT read as a failure, and it
  // must not gate anything: Continue/Submit never wait on income.
  const expiredNote = payrollExpired && !connected && !payrollPending ? (
    <p
      data-testid="payroll-pending-expired"
      className="text-xs text-muted-foreground leading-relaxed"
    >
      {isDocumentUpload
        ? "We're still reviewing your documents — this can take a few hours. You don't need to wait here; your income will appear automatically if it comes through. You can also upload again."
        : "We didn't get your income details this time. You don't need to wait here — they'll appear automatically if they still come through, and you can try connecting again."}
    </p>
  ) : null;

  return (
    <div className="space-y-2">
    <Button
      type="button"
      variant={connected ? "outline" : "default"}
      onClick={handleClick}
      disabled={disabled}
      className="w-full justify-center gap-2"
    >
      {connected ? (
        <>
          <CheckCircle2 className="w-4 h-4 text-trust-green" />
          {isDocumentUpload
            ? "Documents verified"
            : product === "payroll"
              ? "Payroll account connected"
              : "Bank or payroll account connected"}
        </>
      ) : payrollPending ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {isDocumentUpload ? "Reviewing your documents…" : "Retrieving your income…"}
        </>
      ) : busy ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {exchanging ? "Verifying with your bank…" : isDocumentUpload ? "Opening upload…" : "Connecting…"}
        </>
      ) : isDocumentUpload ? (
        <>
          <FileUp className="w-4 h-4" />
          {/* Pay stubs lead: they're recent, where a W-2 is prior-year. */}
          Upload pay stubs or W-2
        </>
      ) : (
        <>
          <Briefcase className="w-4 h-4" />
          {product === "payroll" ? "Connect my payroll account" : "Connect Bank or Payroll Account"}
        </>
      )}
    </Button>
      {expiredNote}
    </div>
  );
}
